import { useState, useEffect } from 'react';
import { Target, Eye, Heart, ArrowRight, Zap, Users, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MissionPanel from './about/panels/MissionPanel';
import VisionPanel from './about/panels/VisionPanel';
import ValuesPanel from './about/panels/ValuesPanel';

const CARDS = [
  {
    id: 'mission',
    tag: 'WHO WE ARE',
    title: 'Our Mission',
    subtitle: 'Democratise AI marketing',
    gridColor: '#2563eb',
    accentColor: 'blue',
    icon: Target,
    metric: { value: '30', label: 'DAYS' },
    features: [
      'Enterprise AI tools at startup prices',
      'Strategy → Design → Publish in one platform',
      'No agency fees, no design skills needed',
      'Built for growing small businesses in the UK',
    ],
  },
  {
    id: 'vision',
    tag: 'WHERE WE\'RE GOING',
    title: 'Our Vision',
    subtitle: 'Lead global AI marketing innovation',
    gridColor: '#16a34a',
    accentColor: 'green',
    icon: Eye,
    metric: { value: 'AI', label: 'FIRST' },
    features: [
      'Every business has world-class marketing',
      'AI that learns and adapts to your brand',
      'Seamless multi-platform publishing',
      'Trusted by thousands of UK small businesses',
    ],
  },
  {
    id: 'values',
    tag: 'HOW WE WORK',
    title: 'Core Values',
    subtitle: 'Innovation · Transparency · Excellence',
    gridColor: '#7c3aed',
    accentColor: 'purple',
    icon: Heart,
    metric: { value: '£39', label: '/MONTH' },
    features: [
      'Honest, transparent pricing — no hidden fees',
      'We build what our customers actually need',
      'Quality over quantity in every feature',
      'Privacy-first — your data is always yours',
    ],
  },
];

const STATS = [
  { value: '1',    label: 'Platform',       sub: 'Strategy to publish'  },
  { value: '30',   label: 'Day Campaign',   sub: 'Complete plan'        },
  { value: '14',   label: 'Text Styles',    sub: 'Design Studio'        },
  { value: '£39',  label: 'Per Month',      sub: 'No hidden fees'       },
];

export default function AboutDigitalTrent() {
  const [isVisible, setIsVisible] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen px-4 md:px-6 pt-28 pb-16" style={{ background: '#060e1e' }}>
      <div className="max-w-7xl mx-auto">

        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800/60 text-xs text-slate-400 font-medium">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
            About Digital Trent — Click any card to learn more
          </div>
        </div>

        {/* Hero heading */}
        <div
          className="text-center mb-10 md:mb-14 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] mb-4">
            We make marketing<br />
            <span style={{
              backgroundImage: 'linear-gradient(90deg,#60a5fa 0%,#34d399 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              accessible.
            </span>
          </h1>
          <p className="text-sm md:text-base text-slate-400 max-w-xl mx-auto leading-relaxed mb-8">
            We're building the AI marketing platform that turns your business brief into a
            published 30-day campaign — with strategy, design, video, and social publishing built in.
          </p>
          <button
            onClick={() => navigate('/auth/signup')}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:-translate-y-0.5"
          >
            Start Your Free 14-Day Trial
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Stats row */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '150ms' }}
        >
          {STATS.map((s, i) => (
            <div key={i} className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-4 text-center">
              <div className="text-2xl md:text-3xl font-extrabold text-white mb-0.5">{s.value}</div>
              <div className="text-sm font-semibold text-slate-300">{s.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* 3-card grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(32px)', transitionDelay: '250ms' }}
        >
          {CARDS.map(card => {
            const Icon = card.icon;
            const isBlue = card.accentColor === 'blue';
            const isPurple = card.accentColor === 'purple';
            const accentBg = isBlue ? 'bg-blue-600' : isPurple ? 'bg-purple-600' : 'bg-green-600';
            const accentText = isBlue ? 'text-blue-400' : isPurple ? 'text-purple-400' : 'text-green-400';
            const hoverBorder = isBlue ? 'hover:border-blue-500/50' : isPurple ? 'hover:border-purple-500/50' : 'hover:border-green-500/50';

            return (
              <div
                key={card.id}
                onClick={() => setActivePanel(card.id)}
                className={`relative bg-slate-800/90 backdrop-blur-sm rounded-xl overflow-hidden border border-slate-700/60 ${hoverBorder} cursor-pointer group transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:bg-slate-800`}
              >
                {/* Micro grid — top-left corner */}
                <div className="absolute top-0 left-0 w-20 h-20 pointer-events-none opacity-30 overflow-hidden">
                  <div
                    style={{
                      backgroundImage: `
                        linear-gradient(${card.gridColor} 0.5px, transparent 0.5px),
                        linear-gradient(90deg, ${card.gridColor} 0.5px, transparent 0.5px)
                      `,
                      backgroundSize: '8px 8px'
                    }}
                    className="absolute inset-0"
                  />
                  <div
                    style={{ background: 'linear-gradient(135deg, transparent 0%, rgba(6,14,30,0.9) 60%)' }}
                    className="absolute inset-0"
                  />
                </div>

                {/* Dark header strip */}
                <div
                  className="relative h-9 overflow-hidden flex items-center justify-between px-3"
                  style={{ backgroundColor: '#0a1526' }}
                >
                  <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest">{card.tag}</span>
                  <span className={`text-[10px] font-bold ${accentText} uppercase tracking-wide`}>
                    {card.metric.value} {card.metric.label}
                  </span>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`${accentBg} p-2 rounded-lg flex-shrink-0`}>
                      <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white leading-tight">{card.title}</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{card.subtitle}</p>
                    </div>
                  </div>

                  <ul className="space-y-1.5 mb-4">
                    {card.features.map((feat, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] text-slate-400 leading-tight">
                        <span className={`${accentText} font-bold flex-shrink-0 mt-0.5`}>✓</span>
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${accentText} group-hover:gap-2.5 transition-all duration-200`}>
                    <span>Read More</span>
                    <ArrowRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Platform feature teaser */}
        <div
          className="mt-10 p-5 md:p-6 bg-slate-800/60 border border-slate-700/60 rounded-xl transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transitionDelay: '400ms' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { icon: Zap,      title: 'AI Strategy',    desc: 'SMART goals, brand positioning, channel mix & 30-day calendar — generated in seconds' },
              { icon: Users,    title: 'Design & Create', desc: 'Design Studio with 12 templates, AI images, 14 text styles, and guided video workflow' },
              { icon: BarChart3, title: 'Publish & Track', desc: 'Direct Instagram & Facebook publishing with campaign dashboard and PDF reports' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex flex-col items-center text-center px-2">
                  <div className="w-9 h-9 bg-blue-600/20 rounded-lg flex items-center justify-center mb-2">
                    <Icon size={16} className="text-blue-400" />
                  </div>
                  <h4 className="text-sm font-bold text-white mb-1">{item.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Sliding Panels */}
      {activePanel === 'mission' && <MissionPanel onClose={() => setActivePanel(null)} />}
      {activePanel === 'vision'  && <VisionPanel  onClose={() => setActivePanel(null)} />}
      {activePanel === 'values'  && <ValuesPanel  onClose={() => setActivePanel(null)} />}
    </div>
  );
}
